import { MutableRefObject, RefObject, useEffect } from 'react'

export interface UseKeyboardAdjustOptions {
  enabled: boolean
  drawerRef: RefObject<HTMLElement | null>
  isKeyboardOpenRef: MutableRefObject<boolean>
}

/**
 * Adjusts a fixed-position drawer when the virtual keyboard opens on
 * iOS Safari, keeping content above the keyboard.
 *
 * When the keyboard opens, it covers the bottom of the screen. This hook
 * detects the keyboard via the visualViewport API and sets the drawer's
 * `bottom` style to push it above the keyboard.
 *
 * Also:
 * - Intercepts touchend on INPUT/TEXTAREA elements to call
 *   focus({ preventScroll: true }), preventing iOS from scrolling the
 *   document when focusing an input (Safari 15.5+).
 * - Blurs the active input when tapping non-input areas inside the drawer,
 *   snapping the drawer back immediately (visualViewport events can lag
 *   1-2s on iOS).
 * - Prevents background scrolling via touchmove listener on touches
 *   outside the drawer.
 * - Sets isKeyboardOpenRef so other hooks (useBottomSheetDrag) can
 *   skip animating `top` while the keyboard is open, preventing iOS
 *   from scrolling the visual viewport during drawer state transitions.
 * - Restores CSS transitions when keyboard closes.
 *
 * Note: there is a known iOS Safari limitation where the drawer resizes
 * slightly ahead of the keyboard animation on open. The iOS keyboard
 * uses a private animation curve that cannot be matched from the web.
 *
 * On Android, the layout viewport resizes natively for the keyboard, so
 * keyboardOffset stays near 0 and this handler is effectively a no-op.
 */
export const useKeyboardAdjust = ({
  enabled,
  drawerRef,
  isKeyboardOpenRef,
}: UseKeyboardAdjustOptions): void => {
  useEffect(() => {
    if (!enabled) return undefined
    const vv = window.visualViewport
    if (!vv) return undefined

    const handleViewportChange = () => {
      const drawer = drawerRef.current
      if (!drawer) return

      const keyboardOffset = window.innerHeight - vv.height - vv.offsetTop
      if (keyboardOffset > 100) {
        // eslint-disable-next-line no-param-reassign
        isKeyboardOpenRef.current = true
        drawer.style.bottom = `${keyboardOffset}px`
      } else if (isKeyboardOpenRef.current) {
        // eslint-disable-next-line no-param-reassign
        isKeyboardOpenRef.current = false
        drawer.style.bottom = ''
        // Restore full CSS transitions (including `top`) now that
        // the keyboard is closed. useBottomSheetDrag may have removed
        // `top` from transitions to prevent iOS visual viewport scroll.
        drawer.style.transition = ''
      }
    }

    // Prevent scrolling that iOS Safari triggers during touch gestures.
    // - Always prevent scrolling for touches outside the drawer.
    // - When keyboard is open, also prevent scrolling for non-scrollable
    //   areas inside the drawer (e.g., drag handle). Without this, iOS
    //   scrolls the visual viewport during drag-to-expand gestures,
    //   pushing the header and video off-screen.
    // - Always allow scrolling inside scrollable content containers
    //   (e.g., chat messages with overflow-y: scroll/auto).
    // Uses document-level listener with { passive: false } so
    // preventDefault() is guaranteed to work.
    const handleTouchMove = (e: TouchEvent) => {
      const drawer = drawerRef.current
      if (!drawer) return

      // Touches outside the drawer — always prevent
      if (!drawer.contains(e.target as Node)) {
        e.preventDefault()
        return
      }

      // Touches inside the drawer when keyboard is open —
      // prevent unless the touch is in a scrollable area
      if (isKeyboardOpenRef.current) {
        let el = e.target as HTMLElement | null
        while (el && el !== drawer) {
          if (el.scrollHeight > el.clientHeight) {
            const { overflowY } = getComputedStyle(el)
            if (overflowY === 'scroll' || overflowY === 'auto') {
              return // Allow — this is a scrollable content area
            }
          }
          el = el.parentElement
        }
        e.preventDefault()
      }
    }

    // Prevent iOS from scrolling the document when focusing an input.
    // iOS programmatically scrolls the page to bring focused inputs into
    // view. focus({ preventScroll: true }) suppresses this (Safari 15.5+).
    // We intercept touchend on inputs inside the drawer, prevent the
    // default focus, and re-focus with preventScroll.
    // Also: tapping a non-input area while the keyboard is open blurs
    // the active input and snaps the drawer back immediately.
    const handleTouchEnd = (e: Event) => {
      const target = e.target as HTMLElement
      const tag = target.tagName
      if (tag === 'TEXTAREA' || tag === 'INPUT' || target.isContentEditable) {
        e.preventDefault()
        target.focus({ preventScroll: true })
      } else if (isKeyboardOpenRef.current) {
        const active = document.activeElement as HTMLElement | null
        if (active && active !== document.body) active.blur()
        // eslint-disable-next-line no-param-reassign
        isKeyboardOpenRef.current = false
        const d = drawerRef.current
        if (d) {
          d.style.bottom = ''
          d.style.transition = ''
        }
      }
    }

    const drawer = drawerRef.current
    vv.addEventListener('resize', handleViewportChange)
    vv.addEventListener('scroll', handleViewportChange)
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    drawer?.addEventListener('touchend', handleTouchEnd, { capture: true })

    return () => {
      vv.removeEventListener('resize', handleViewportChange)
      vv.removeEventListener('scroll', handleViewportChange)
      document.removeEventListener('touchmove', handleTouchMove)
      drawer?.removeEventListener('touchend', handleTouchEnd, { capture: true })
      // eslint-disable-next-line no-param-reassign
      isKeyboardOpenRef.current = false
      if (drawer) {
        drawer.style.bottom = ''
        drawer.style.transition = ''
      }
    }
  }, [enabled, drawerRef, isKeyboardOpenRef])
}
